import {isFilled} from '@prismicio/client'
import {SliceZone} from '@prismicio/react'
import type {
  ArticleDocument,
  BlogPostDocument,
  ButtonDocument,
  CustomerStoryDocument,
  DialogDocumentData,
  EventDocument,
  GenericFormDocument,
  NavigationDocumentData,
  PageDocument,
  ProductAnnouncementCardDocument,
  ProductDeepDiveDocumentData,
} from 'prismicio-types'
import {memo} from 'react'
import {components} from 'slices'
import type {FraudGameQuestionDocument} from 'slices/FraudGame/data'
import {getButtonsData} from 'src/common/helpers'
import {Layout} from 'src/common/Layout'
import {Meta} from 'src/common/Meta'
import {PageFaqSEO} from 'src/common/PageFaqSEO'
import type {ParentUid} from 'src/common/utils/documentHelpers'
import {getAlternateLanguagesWithParent, getMetaProps} from 'src/common/utils/documentHelpers'
import type {EventType} from 'src/common/utils/eventRoutes'

/**
 * Page Component - Renders PageDocument type only
 *
 * This component is now type-safe and only handles PageDocument.
 * For other document types, see PageRouter component.
 */
export const Page = memo(function (props: {
  articles?: Array<ArticleDocument>
  customerStories?: Array<CustomerStoryDocument>
  customerStoriesData?: Array<CustomerStoryDocument>
  navigationData: NavigationDocumentData
  page: PageDocument
  parentUids?: Array<ParentUid>
  productDeepDiveData?: ProductDeepDiveDocumentData
  formData?: GenericFormDocument
  dialogData?: DialogDocumentData
  productAnnouncementsData?: Array<ProductAnnouncementCardDocument>
  fraudGameQuestions?: FraudGameQuestionDocument[]
  buttonsData?: Array<ButtonDocument>
  blogPosts?: Array<BlogPostDocument>
  categories?: Array<{category_name: string; value: string}>
  totalPages?: number
  // Event-specific props
  eventsByType?: Record<string, EventDocument<string>[]>
  eventTypes?: EventType[]
  customBackground?: string
}) {
  const data = props.page.data

  const {primaryButtonData: primaryNavButton, secondaryButtonData: secondaryNavButton} = getButtonsData({
    buttonsData: props.buttonsData,
    primaryButton: data.primary_nav_button,
    secondaryButton: data.secondary_nav_button,
  })

  return (
    <Layout
      alternateLanguages={getAlternateLanguagesWithParent(props.page.alternate_languages, props.parentUids)}
      breadcrumbs={data.breadcrumbs}
      navigationData={props.navigationData}
      hideNavigation={!!data.is_navigation_hidden}
      hideFooter={!!data.is_footer_hidden}
      mainClassName={data.column_layout ? 'flex flex-col md:flex-row' : ''}
      isDarkNavigation={data.is_dark_navigation || false}
      pageUid={props.page.uid}
      locale={props.page.lang}
      parentUids={props.parentUids}
      forceFooterV2={data.force_footer_v2}
      funnelType={data.funnel_type}
      canonicalUrl={data.canonical_link}
      primaryNavButton={primaryNavButton}
      secondaryNavButton={secondaryNavButton}
      hidePrimaryNavButton={!!data.hide_primary_nav_button}
      hideSecondaryNavButton={!!data.hide_secondary_nav_button}
      primaryNavButtonScrollText={data.primary_nav_button_scroll_into_view_text}
      primaryNavButtonScrollId={data.primary_nav_button_scroll_into_view_id}
      secondaryNavButtonScrollText={data.secondary_nav_button_scroll_into_view_text}
      secondaryNavButtonScrollId={data.secondary_nav_button_scroll_into_view_id}
      customBackground={props.customBackground}
      desktopBackground={
        isFilled.linkToMedia(data.desktop_background_image) ? data.desktop_background_image.url : undefined
      }
      mobileBackground={
        isFilled.linkToMedia(data.mobile_background_image) ? data.mobile_background_image.url : undefined
      }
    >
      <Meta {...getMetaProps(props.page)} noIndex={data.disable_indexing} />
      <PageFaqSEO slices={data.slices} />

      <SliceZone
        components={components}
        context={{
          pageUid: props.page.uid,
          articles: props.articles ?? [],
          customerStories: props.customerStories,
          customerStoriesData: props.customerStoriesData,
          top_features: data.top_features,
          features_label: data.features_label,
          features: data.features,
          other_features_label: data.other_features_label,
          other_features: data.other_features,
          pricing_plan_1: data.pricing_plan_1,
          pricing_plan_2: data.pricing_plan_2,
          pricing_plan_3: data.pricing_plan_3,
          product_deep_dive_data: props.productDeepDiveData,
          navigation_data: props.navigationData,
          form_data: props.formData,
          dialog_data: props.dialogData,
          product_announcements_data: props.productAnnouncementsData,
          fraudGameQuestions: props.fraudGameQuestions,
          buttonsData: props.buttonsData,
          parentPage: data.parent_page,
          blogPosts: props.blogPosts,
          categories: props.categories,
          totalPages: props.totalPages,
          // Event-specific context
          eventsByType: props.eventsByType,
          eventTypes: props.eventTypes,
        }}
        slices={data.slices}
      />
    </Layout>
  )
})
